import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from 'src/app/material.module';
import { UserRecordingService, Recording } from './user-recording.service';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';

@Component({
  selector: 'app-user-recording',
  standalone: true,
  imports: [CommonModule, MaterialModule, FormsModule],
  templateUrl: './user-recording.component.html',
  styleUrls: ['./user-recording.component.scss']
})
export class UserRecordingComponent implements OnInit, OnDestroy {

  recordings: Recording[] = [];
  selectedRecording: Recording | null = null;

  totalRecords = 0;
  pageSize = 10;
  pageIndex = 0; // 0-based for mat-paginator
  pageSizeOptions = [5, 10, 20, 50];
  isLoading = false;

  // Search filter
  searchTerm: string = '';
  private searchSubject = new Subject<string>();
  private searchSub: Subscription = Subscription.EMPTY;

  constructor(private userRecordingService: UserRecordingService) {}

  ngOnInit(): void {
    this.fetchRecordings();

    // Debounced search stream
    this.searchSub = this.searchSubject
      .pipe(
        debounceTime(350),
        distinctUntilChanged()
      )
      .subscribe((query) => {
        this.pageIndex = 0;
        this.fetchRecordings(query);
      });
  }

  ngOnDestroy(): void {
    this.searchSub.unsubscribe();
  }

  /**
   * Filtered recordings list by video name and user name
   */
  get filteredRecordings(): Recording[] {
    if (!this.searchTerm || !this.searchTerm.trim()) {
      return this.recordings;
    }
    const query = this.searchTerm.trim().toLowerCase();
    return this.recordings.filter((rec) => {
      const videoName = (rec.title || '').toLowerCase();
      const userName = (rec.profile?.username || rec.profile?.fullName || '').toLowerCase();
      return videoName.includes(query) || userName.includes(query);
    });
  }

  /**
   * Fetch recordings with pagination & optional backend search
   */
  fetchRecordings(search?: string): void {
    this.isLoading = true;
    const pageNo = this.pageIndex + 1;
    const term = search !== undefined ? search : this.searchTerm;

    this.userRecordingService.getRecordings(pageNo, this.pageSize, term).subscribe({
      next: (res) => {
        // Backend currently ignores pageSize and always returns a fixed-size page,
        // so trim client-side until that's fixed server-side.
        this.recordings = (res.items || []).slice(0, this.pageSize);
        this.totalRecords = res.totalRecords ?? res.total ?? res.count ?? this.recordings.length;
        this.isLoading = false;

        // Auto-select first video if none selected or previous selection no longer exists
        if (this.recordings.length > 0 && (!this.selectedRecording || !this.recordings.some(r => r.id === this.selectedRecording?.id))) {
          this.selectedRecording = this.recordings[0];
        } else if (this.recordings.length === 0) {
          this.selectedRecording = null;
        }
      },
      error: (err) => {
        console.error('Failed to fetch recordings:', err);
        this.isLoading = false;
      }
    });
  }

  /**
   * Handle user typing in search input
   */
  onSearchInput(value: string): void {
    this.searchSubject.next(value);
  }

  /**
   * Clear search bar
   */
  clearSearch(): void {
    this.searchTerm = '';
    this.pageIndex = 0;
    this.fetchRecordings('');
  }

  onPageChange(event: any): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.fetchRecordings();
  }

  selectRecording(recording: Recording): void {
    this.selectedRecording = recording;
  }

  closeVideo(): void {
    this.selectedRecording = null;
  }

  formatDuration(seconds: string | number): string {
    const sec = typeof seconds === 'number' ? seconds : parseInt(seconds, 10) || 0;
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }
}